/**
 * APEXX01 Dark Theme JavaScript
 * Handles interactivity for mobile menu, accordion, cart, and more
 */

(function() {
  'use strict';

  // ==========================================================================
  // Mobile Menu
  // ==========================================================================
  const mobileMenu = {
    init() {
      const menu = document.querySelector('[data-mobile-menu]');
      const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      const openIcon = document.querySelector('[data-menu-icon-open]');
      const closeIcon = document.querySelector('[data-menu-icon-close]');

      if (!menu || !toggleBtn) return;

      // Toggle menu on button click
      toggleBtn.addEventListener('click', () => {
        if (menu.classList.contains('is-open')) {
          this.close(menu, toggleBtn, openIcon, closeIcon);
        } else {
          this.open(menu, toggleBtn, openIcon, closeIcon);
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) {
          this.close(menu, toggleBtn, openIcon, closeIcon);
        }
      });

      // Close when clicking outside menu (on overlay background)
      menu.addEventListener('click', (e) => {
        if (e.target === menu) {
          this.close(menu, toggleBtn, openIcon, closeIcon);
        }
      });

      // Mobile dropdown toggles
      const dropdownTriggers = menu.querySelectorAll('[data-mobile-dropdown-trigger]');
      dropdownTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const content = trigger.nextElementSibling;
          const icon = trigger.querySelector('svg');

          content.classList.toggle('hidden');
          if (icon) {
            icon.style.transform = content.classList.contains('hidden') ? '' : 'rotate(180deg)';
          }
        });
      });
    },

    open(menu, trigger, openIcon, closeIcon) {
      menu.classList.add('is-open');
      menu.removeAttribute('inert');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      // Toggle icons
      if (openIcon) openIcon.classList.add('hidden');
      if (closeIcon) closeIcon.classList.remove('hidden');
      // Focus first link for accessibility
      const firstLink = menu.querySelector('a, button');
      if (firstLink) firstLink.focus();
    },

    close(menu, trigger, openIcon, closeIcon) {
      menu.classList.remove('is-open');
      menu.setAttribute('inert', '');
      trigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      // Toggle icons
      if (openIcon) openIcon.classList.remove('hidden');
      if (closeIcon) closeIcon.classList.add('hidden');
      // Return focus to trigger
      trigger.focus();
    }
  };

  // ==========================================================================
  // Accordion
  // ==========================================================================
  const accordion = {
    init() {
      const accordions = document.querySelectorAll('[data-accordion]');

      accordions.forEach(accordion => {
        const triggers = accordion.querySelectorAll('[data-accordion-trigger]');

        triggers.forEach(trigger => {
          trigger.addEventListener('click', () => {
            const item = trigger.closest('[data-accordion-item]');
            const isOpen = item.classList.contains('is-open');

            // Close all items in this accordion
            accordion.querySelectorAll('[data-accordion-item]').forEach(i => {
              i.classList.remove('is-open');
              i.querySelector('[data-accordion-trigger]').setAttribute('aria-expanded', 'false');
            });

            // Open clicked item if it was closed
            if (!isOpen) {
              item.classList.add('is-open');
              trigger.setAttribute('aria-expanded', 'true');
            }
          });
        });
      });
    }
  };

  // ==========================================================================
  // Cart Drawer
  // ==========================================================================
  const cartDrawer = {
    init() {
      const drawer = document.querySelector('[data-cart-drawer]');
      const openTriggers = document.querySelectorAll('[data-cart-icon]');
      const closeTriggers = document.querySelectorAll('[data-cart-drawer-close]');

      if (!drawer) return;

      openTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          if (window.routes && window.routes.cart_url) {
            // If cart drawer exists, prevent default and open drawer
            e.preventDefault();
            this.open(drawer);
          }
        });
      });

      closeTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => this.close(drawer));
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
          this.close(drawer);
        }
      });
    },

    open(drawer) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close(drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  };

  // ==========================================================================
  // Quantity Buttons
  // ==========================================================================
  const quantityButtons = {
    init() {
      document.addEventListener('click', (e) => {
        const minusBtn = e.target.closest('[data-quantity-minus]');
        const plusBtn = e.target.closest('[data-quantity-plus]');

        if (minusBtn) {
          const input = minusBtn.parentElement.querySelector('input');
          if (input && parseInt(input.value) > parseInt(input.min || 0)) {
            input.value = parseInt(input.value) - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        if (plusBtn) {
          const input = plusBtn.parentElement.querySelector('input');
          if (input) {
            input.value = parseInt(input.value) + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }
  };

  // ==========================================================================
  // Product Gallery
  // ==========================================================================
  const productGallery = {
    currentIndex: 0,

    init() {
      const gallery = document.querySelector('[data-product-gallery]');
      if (!gallery) return;

      this.gallery = gallery;
      this.mainImage = gallery.querySelector('[data-main-image]');
      this.thumbnails = Array.from(gallery.querySelectorAll('[data-thumbnail]'));
      this.thumbnailsContainer = gallery.querySelector('[data-product-thumbnails]');
      this.prevBtn = gallery.querySelector('[data-gallery-prev]');
      this.nextBtn = gallery.querySelector('[data-gallery-next]');
      this.currentIndex = 0;

      if (!this.mainImage || this.thumbnails.length === 0) return;

      // Thumbnail click handlers
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          this.goToSlide(index);
        });
      });

      // Arrow navigation
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prevSlide());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.nextSlide());
      }
    },

    goToSlide(index) {
      if (index < 0 || index >= this.thumbnails.length) return;

      this.currentIndex = index;
      const thumb = this.thumbnails[index];
      const newSrc = thumb.getAttribute('data-thumbnail');

      if (this.mainImage && newSrc) {
        this.mainImage.src = newSrc;
      }

      // Update active state
      this.thumbnails.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');

      // Scroll thumbnail into view
      this.scrollThumbnailIntoView(index);
    },

    prevSlide() {
      const newIndex = this.currentIndex === 0
        ? this.thumbnails.length - 1
        : this.currentIndex - 1;
      this.goToSlide(newIndex);
    },

    nextSlide() {
      const newIndex = this.currentIndex === this.thumbnails.length - 1
        ? 0
        : this.currentIndex + 1;
      this.goToSlide(newIndex);
    },

    scrollThumbnailIntoView(index) {
      if (!this.thumbnailsContainer) return;

      const thumb = this.thumbnails[index];
      const containerWidth = this.thumbnailsContainer.offsetWidth;
      const thumbWidth = thumb.offsetWidth;
      const thumbLeft = thumb.offsetLeft;

      // Calculate scroll position to center the thumbnail
      const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

      this.thumbnailsContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }
  };

  // ==========================================================================
  // Product Variant Selection
  // ==========================================================================
  const variantSelector = {
    init() {
      const form = document.querySelector('[data-product-form]');
      if (!form) return;

      const productJson = document.querySelector('[data-product-json]');
      if (!productJson) return;

      const product = JSON.parse(productJson.textContent);
      const variantButtons = form.querySelectorAll('.variant-button');

      variantButtons.forEach(button => {
        button.addEventListener('click', () => {
          const optionContainer = button.closest('[data-option-index]');

          // Update button states
          optionContainer.querySelectorAll('.variant-button').forEach(btn => {
            btn.classList.remove('is-selected');
          });
          button.classList.add('is-selected');

          // Find matching variant
          const selectedOptions = [];
          form.querySelectorAll('[data-option-index]').forEach(container => {
            const selectedBtn = container.querySelector('.variant-button.is-selected');
            if (selectedBtn) {
              selectedOptions.push(selectedBtn.getAttribute('data-option-value'));
            }
          });

          const selectedVariant = product.variants.find(variant => {
            return variant.options.every((opt, index) => opt === selectedOptions[index]);
          });

          if (selectedVariant) {
            this.updateVariant(form, selectedVariant);
          }
        });
      });
    },

    updateVariant(form, variant) {
      // Update hidden input
      const variantInput = form.querySelector('[data-variant-id]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // Update price
      const priceEl = document.querySelector('[data-product-price]');
      if (priceEl && window.Shopify && window.Shopify.formatMoney) {
        priceEl.textContent = window.Shopify.formatMoney(variant.price);
      }

      // Update compare price
      const comparePriceEl = document.querySelector('[data-product-compare-price]');
      if (comparePriceEl) {
        if (variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = window.Shopify.formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      // Update add to cart button
      const addToCartBtn = form.querySelector('[data-add-to-cart]');
      if (addToCartBtn) {
        if (variant.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = window.cartStrings?.add_to_cart || 'Add to Cart';
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = window.cartStrings?.sold_out || 'Sold Out';
        }
      }
    }
  };

  // ==========================================================================
  // Sticky Header
  // ==========================================================================
  const stickyHeader = {
    init() {
      const header = document.querySelector('[data-header]');
      if (!header) return;

      let lastScrollY = window.scrollY;

      window.addEventListener('scroll', () => {
        const currentScrollY = window.scrollY;

        if (currentScrollY > 100) {
          header.classList.add('is-scrolled');
        } else {
          header.classList.remove('is-scrolled');
        }

        lastScrollY = currentScrollY;
      }, { passive: true });
    }
  };

  // ==========================================================================
  // Sticky Product Bar
  // ==========================================================================
  const stickyBar = {
    init() {
      const bar = document.querySelector('.sticky-bar');
      if (!bar) return;

      const trigger = document.querySelector('.product-info');
      if (!trigger) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            bar.classList.remove('is-visible');
          } else {
            bar.classList.add('is-visible');
          }
        });
      }, {
        threshold: 0,
        rootMargin: '-100px 0px 0px 0px'
      });

      observer.observe(trigger);
    }
  };

  // ==========================================================================
  // FAQ Accordion
  // ==========================================================================
  const faqAccordion = {
    init() {
      const accordionItems = document.querySelectorAll('.faq-accordion-item');
      if (!accordionItems.length) return;

      accordionItems.forEach(item => {
        const trigger = item.querySelector('.faq-accordion-trigger');
        const content = item.querySelector('.faq-accordion-content');
        const icon = item.querySelector('.faq-accordion-icon');

        if (!trigger || !content) return;

        trigger.addEventListener('click', () => {
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          // Close all other items
          accordionItems.forEach(otherItem => {
            if (otherItem !== item) {
              const otherTrigger = otherItem.querySelector('.faq-accordion-trigger');
              const otherContent = otherItem.querySelector('.faq-accordion-content');
              const otherIcon = otherItem.querySelector('.faq-accordion-icon');

              if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
              if (otherContent) {
                otherContent.style.maxHeight = '0';
                otherContent.style.opacity = '0';
              }
              if (otherIcon) otherIcon.style.transform = 'rotate(0deg)';
            }
          });

          // Toggle current item
          if (isOpen) {
            trigger.setAttribute('aria-expanded', 'false');
            content.style.maxHeight = '0';
            content.style.opacity = '0';
            if (icon) icon.style.transform = 'rotate(0deg)';
          } else {
            trigger.setAttribute('aria-expanded', 'true');
            content.style.maxHeight = content.scrollHeight + 'px';
            content.style.opacity = '1';
            if (icon) icon.style.transform = 'rotate(180deg)';
          }
        });
      });
    }
  };

  // ==========================================================================
  // Policy Page Auto-Styling
  // ==========================================================================
  const policyPage = {
    init() {
      // Auto-style numbered items (1.1., 1.2., etc.) in policy blocks
      document.querySelectorAll('.policy-block-content p').forEach(function(p) {
        var html = p.innerHTML;
        html = html.replace(/^(\d+\.\d+\.)\s*/g, '<span class="policy-number">$1</span> ');
        p.innerHTML = html;
      });

      // Auto-style the orange highlight text in intro
      document.querySelectorAll('.policy-intro-text p').forEach(function(p) {
        var html = p.innerHTML;
        html = html.replace(/(not satisfied[^.]+required)/gi, '<span class="policy-orange-text">$1</span>');
        p.innerHTML = html;
      });
    }
  };

  // ==========================================================================
  // Initialize
  // ==========================================================================
  document.addEventListener('DOMContentLoaded', () => {
    mobileMenu.init();
    accordion.init();
    faqAccordion.init();
    cartDrawer.init();
    quantityButtons.init();
    productGallery.init();
    variantSelector.init();
    stickyHeader.init();
    stickyBar.init();
    policyPage.init();
  });

})();
